import { useMemo } from "react";

import { KanbanColumnId, KanbanTask } from "../../../../../widget";
import { KANBAN_COLUMN_ORDER } from "../../../../../widget/general/TasksDashboard/constants";
import { TasksDashboardGroup, TasksDashboardQueryFilters } from "../models";
import { getGroupTasksKey, mapColumnIdToStatus } from "../utils";
import { useWorkflowTaskGroups } from "./useWorkflowTaskGroups";

/** One status section within a List group — the level the List view expands and pages at. */
export interface TasksDashboardListSection {
  columnId: KanbanColumnId;
  /** The `TaskConstants` status string this section's rows are loaded with. */
  status: string;
  /** Server count for the section, from the group's `statusCounts`. */
  count: number;
  tasks: KanbanTask[];
  isLoadingTasks: boolean;
  hasMoreTasks: boolean;
}

export interface TasksDashboardListGroup {
  objectUuid: string;
  objectName: string;
  objectType: string;
  /** Server total for the whole group. */
  taskCount: number;
  milestoneTaskCount: number;
  /** Server total of tasks past their deadline and not complete. */
  overdueTaskCount: number;
  /** Sections in fixed lane order, with the empty statuses dropped. */
  sections: TasksDashboardListSection[];
}

export interface UseTasksDashboardListArgs {
  /** The one shared task-level filter object (BR-02828). Must be referentially stable. */
  filters: TasksDashboardQueryFilters;
  /** False unless the List is the view on screen, so no group query is issued for other views. */
  enabled: boolean;
  pageSize?: number;
  refreshKey?: number;
}

export interface UseTasksDashboardListReturnData {
  listGroups: TasksDashboardListGroup[];
  /** The raw group headers. */
  groups: TasksDashboardGroup[];
  isLoadingGroups: boolean;
  groupsHaveNextPage: boolean;
  fetchMoreGroups: () => void;
  /** Loads one status section's rows — called when the List expands it. */
  onSectionExpand: (objectUuid: string, status: string) => void;
  /** Loads a status section's next page of rows. */
  onSectionShowMore: (objectUuid: string, status: string) => void;
}

/**
 * The List view's data: the same server group headers as the Gantt, sliced into status sections.
 * The List groups by object and then by status, so its leaves are loaded per `(object, status)` —
 * each section's count is the server's `statusCounts` entry, never the rows loaded under it
 * (BR-02827).
 */
export const useTasksDashboardList = ({
  filters,
  enabled,
  pageSize,
  refreshKey,
}: UseTasksDashboardListArgs): UseTasksDashboardListReturnData => {
  const {
    groups,
    isLoadingGroups,
    groupsHaveNextPage,
    fetchMoreGroups,
    groupTasks,
    loadGroupTasks,
    fetchMoreGroupTasks,
  } = useWorkflowTaskGroups({ filters, enabled, pageSize, refreshKey });

  const listGroups = useMemo<TasksDashboardListGroup[]>(
    () =>
      groups.map((group) => {
        const countsByStatus = new Map(group.statusCounts.map(({ status, count }) => [status, count]));

        return {
          objectUuid: group.objectUuid,
          objectName: group.objectName ?? "",
          objectType: group.objectType,
          taskCount: group.taskCount,
          milestoneTaskCount: group.milestoneTaskCount,
          overdueTaskCount: group.overdueTaskCount,
          sections: KANBAN_COLUMN_ORDER.reduce<TasksDashboardListSection[]>((sections, columnId) => {
            const status = mapColumnIdToStatus(columnId);
            const count = countsByStatus.get(status) ?? 0;
            // A status the group has no tasks in gets no section — an empty header would be noise.
            if (count === 0) return sections;

            const leaves = groupTasks[getGroupTasksKey(group.objectUuid, status)];

            return [
              ...sections,
              {
                columnId,
                status,
                count,
                tasks: leaves?.tasks ?? [],
                isLoadingTasks: leaves?.isLoading ?? false,
                hasMoreTasks: leaves?.hasNextPage ?? false,
              },
            ];
          }, []),
        };
      }),
    [groups, groupTasks]
  );

  return {
    listGroups,
    groups,
    isLoadingGroups,
    groupsHaveNextPage,
    fetchMoreGroups,
    onSectionExpand: loadGroupTasks,
    onSectionShowMore: fetchMoreGroupTasks,
  };
};
